import localforage from 'localforage';
import * as Sentry from '@sentry/browser';

const STORED_PHOTO_KIND = 'photo-v2';
const DEFAULT_MIME = 'image/jpeg';

// Store photos as ArrayBuffers rather than raw Blob/File instances.
// This avoids relying on Chromium's IndexedDB blob backing store, which has
// been the suspected source of Android "InvalidBlob" write failures.
export async function savePhoto(key, photo, metadata = {}) {
  const storageEstimate = await getStorageEstimate();
  let storedPhoto;

  try {
    storedPhoto = await serialisePhoto(photo);
  } catch (err) {
    // Conversion failures are distinct from IndexedDB failures. Capturing both
    // makes it easier to tell whether the source file was invalid or storage broke.
    Sentry.captureException(err, {
      tags: { area: 'photo-storage', photo_storage_version: STORED_PHOTO_KIND },
      extra: {
        key,
        ...metadata,
        errorName: err?.name,
        errorMessage: err?.message,
        sourceConstructor: photo?.constructor?.name,
        sourceSize: photo?.size,
        sourceType: photo?.type,
        storageEstimate
      }
    });
    throw err;
  }

  // Breadcrumbs give context to any later upload/storage exception without
  // attaching the binary payload itself to Sentry.
  Sentry.addBreadcrumb({
    category: 'photo-storage',
    message: 'saving photo',
    level: 'info',
    data: {
      key,
      ...metadata,
      sourceConstructor: photo?.constructor?.name,
      sourceSize: photo?.size,
      sourceType: photo?.type,
      storedSize: storedPhoto.size,
      storedType: storedPhoto.mime,
      storageUsage: storageEstimate?.usage,
      storageQuota: storageEstimate?.quota
    }
  });

  try {
    await localforage.setItem(key, storedPhoto);
  } catch (err) {
    // This is the failure path we expect to see for quota pressure, corrupted
    // IndexedDB state, or browser storage regressions.
    Sentry.captureException(err, {
      tags: { area: 'photo-storage', photo_storage_version: STORED_PHOTO_KIND },
      extra: {
        key,
        ...metadata,
        errorName: err?.name,
        errorMessage: err?.message,
        sourceConstructor: photo?.constructor?.name,
        sourceSize: photo?.size,
        sourceType: photo?.type,
        storedSize: storedPhoto.size,
        storageEstimate
      }
    });
    throw err;
  }

  return storedPhoto;
}

export async function readPhotoBlob(key) {
  const storedPhoto = await localforage.getItem(key);
  return normalisePhotoBlob(storedPhoto, key);
}

// Consumers still need a Blob for object URLs and FormData uploads.
// Keep this backwards-compatible with photos saved before photo-v2, where
// localforage stored the raw Blob/File object directly.
export function normalisePhotoBlob(storedPhoto, key = 'unknown') {
  if (!storedPhoto) {
    throw new Error(`Missing stored photo for key ${key}`);
  }

  if (typeof Blob !== 'undefined' && storedPhoto instanceof Blob) {
    return storedPhoto;
  }

  if (storedPhoto.data) {
    return new Blob([storedPhoto.data], { type: storedPhoto.mime || DEFAULT_MIME });
  }

  throw new Error(`Unexpected stored photo format for key ${key}`);
}

async function serialisePhoto(photo) {
  if (!photo?.arrayBuffer) {
    throw new Error('Cannot store photo: expected Blob/File with arrayBuffer()');
  }

  const data = await photo.arrayBuffer();

  // Keep metadata with the binary payload so inspector/upload code can report
  // size and MIME type without depending on the original File object.
  return {
    kind: STORED_PHOTO_KIND,
    mime: photo.type || DEFAULT_MIME,
    size: photo.size || data.byteLength,
    createdAt: Date.now(),
    data
  };
}

async function getStorageEstimate() {
  // Older browsers may not expose StorageManager. This is diagnostic only, so
  // absence or failure should never block the actual photo save.
  if (!navigator.storage?.estimate) {
    return null;
  }

  try {
    return await navigator.storage.estimate();
  } catch (err) {
    return null;
  }
}
